/** English strings for the partners slice (namespace: partners.*). */
export const partnersEn = {
  title: "Partner group",
  loading: "Loading…",
  retry: "Retry",
  loadError: "Something went wrong while loading. Please try again.",
  actionFailed: "The action could not be completed. Please try again.",

  tabs: {
    groups: "My groups",
    superUsers: "Regional partners",
  },

  accept: {
    heading: "Accept invitation",
    body: "You have been invited to join Activateur. Accept the invitation to continue.",
    accept: "Accept invitation",
    accepting: "Accepting…",
    success: "Invitation accepted.",
    error: "This invitation could not be accepted.",
    done: "You're all set. You can now return to your dashboard.",
  },

  groups: {
    heading: "Partner groups",
    subtitle: "Manage your groups, their members and their license pool.",
    newGroup: "New group",
    create: "Create group",
    creating: "Creating…",
    cancel: "Cancel",
    empty: "No partner groups yet.",
    emptyHint: "Create your first group to start inviting members.",
    open: "Open",
    nameLabel: "Group name",
    namePlaceholder: "e.g. Montréal region",
    maxLicensesLabel: "Maximum licenses",
    maxLicensesHint: "Leave empty for no limit.",
    colName: "Name",
    colCode: "Partner code",
    colMembers: "Members",
    colLicenses: "Licenses",
    colOwner: "Owner",
    noCode: "—",
  },

  detail: {
    back: "Back to groups",
    history: "History",
    settings: "Group settings",
    partnerCode: "Partner code",
    owner: "Owner",
    delete: "Delete group",
    confirmDelete:
      "Permanently delete this group? This cannot be undone.",
    deleting: "Deleting…",
    members: "Members",
    licensePool: "License pool",
    licensePoolSummary: "{{used}} of {{max}} licenses used",
    licensePoolUnlimited: "{{used}} licenses used (no limit)",
    poolThisGroup: "Group licenses",
    poolOwnerPool: "Total licenses (all groups)",
    groupMembers: "Group members",
    poolUsedOfMax: "{{used}} / {{max}}",
    poolAvailable: "{{count}} available",
    poolUnlimited: "{{used}} used · unlimited",
    inviteMember: "Invite a member",
    dangerZone: "Danger zone",
    deleteWarning:
      "Permanently delete this group and all its data. This cannot be undone.",
  },

  invitationEmail: {
    discard: "Your unsaved changes will be lost. Continue?",
    help: "This is the email the invitee will receive. You can edit it before sending; the invitation button is added automatically below your message.",
    existingAccountNote:
      "If this address already has an Activateur account, the person will instead receive a notice asking them to accept the invitation in the app.",
    body: "Message",
    saveAsTemplate: "Save as template",
    reset: "Restore the default text",
    saved: "Template saved for this group.",
    saveFailed: "Could not save the email.",
    loading: "Loading the email…",
    loadFailed: "Could not load the email.",
  },
  members: {
    // Why a member no longer pays. Same words as the Django chips
    // (self_diagnostic.licensing.SUBSCRIPTION_STATE_LABELS).
    subscriptionState: {
      cancelled: "Cancelled",
      expired: "Expired",
    },
    empty: "No active members yet.",
    colName: "Member",
    colEmail: "Email",
    colRole: "Role",
    colLicense: "Licence",
    hasLicense: "Licensed",
    noLicense: "No licence",
    externalLicense: "External licence",
    externalLicenseHint: "Holds a licence from another group.",
    owner: "Owner",
    grant: "Grant licence",
    revokeLicense: "Revoke licence",
    revokeMember: "Remove member",
    confirmRevokeMember: "Remove {{name}} from this group?",
    working: "Working…",
    roleMember: "Member",
    roleLocalPartner: "Local partner",
    rolePartner: "Partner",
    // on-behalf data-consultation links
    linkResults: "Results",
    linkActionPlan: "Action plan",
    linkSelfAssessment: "Self-assessment",
    noSharedData: "No shared data",
    consentNotGranted: "Consultation not granted",
    // manage modal
    manage: "Manage",
    manageTitle: "Manage · {{name}}",
    licenceLabel: "Licence",
    startedWarning:
      "This member has started their self-assessment — removing them keeps their data but ends your access.",
    close: "Close",
  },

  memberData: {
    banner: "Viewing {{name}}'s data",
    readOnly: "Read-only — this member hasn't granted edit consent.",
    back: "Back to group",
    history: "History",
    viewLabel: "Member data",
    tabs: {
      diagnostic: "Self-assessment",
      plan: "Action plan",
    },
  },

  invite: {
    heading: "Invite someone",
    emailLabel: "Email address",
    emailPlaceholder: "name@example.com",
    roleLabel: "Role",
    roleMember: "Member",
    roleLocalPartner: "Local partner",
    submit: "Send invitation",
    sending: "Sending…",
    sent: "Invitation sent to {{email}}.",
    confirmPromoteTitle: "Existing user",
    confirmPromoteBody:
      "An account already exists with this email address. Do you want to invite it to become a local partner of your group? It will become one if it accepts the invitation from its account.",
    confirmPromote: "Send invitation",
    promoteName: "Name",
    promoteEmail: "Email",
    promoteEnterprise: "Company",
    promoteAddress: "Address",
    promotePhone: "Phone",
    cancel: "Cancel",
    pending: "Pending invitations",
    resend: "Resend",
    resending: "Resending…",
    resent: "Invitation resent.",
    cancelInvite: "Cancel",
    cancelling: "Cancelling…",
    cancelled: "Invitation cancelled.",
  },

  groupInvitations: {
    colEmail: "Email",
    colSent: "Sent on",
    colExpires: "Expires on",
    colStatus: "Status",
    status: {
      pending: "Pending",
      expired: "Expired",
      declined: "Declined",
      licence_cap: "Licences used up",
      accepted: "Accepted",
    },
    confirmCancel: "Confirm cancellation of this invitation?",
  },

  superUsers: {
    heading: "Regional partners",
    subtitle: "Invite and manage regional partners and their license pools.",
    adminOnly: "Only administrators can manage regional partners.",
    empty: "No regional partners yet.",
    colName: "Name",
    colEmail: "Email",
    colMaxGroups: "Max groups",
    colMaxLicenses: "Max licenses/group",
    edit: "Edit",
    invite: "Invite a regional partner",
    unlimited: "No limit",
    editHeading: "Pool settings — {{name}}",
    maxGroupsLabel: "Maximum groups",
    maxLicensesLabel: "Maximum licenses per group",
    discountTypeLabel: "Discount type",
    discountPercentage: "Percentage",
    discountFixed: "Fixed amount",
    discountValueLabel: "Discount value",
    discountDurationLabel: "Discount duration (years)",
    close: "Close",
  },

  localPartners: {
    heading: "Local partners",
    empty: "No local partners yet.",
    colGroups: "Group(s)",
    // Matches the Django .po's existing translation of "Licences du groupe",
    // which this label reuses rather than introducing a second wording.
    groupLicences: "Group licenses",
    editHeading: "Settings — {{name}}",
  },

  inviteSuperUser: {
    heading: "Invite a regional partner",
    emailLabel: "Email address",
    submit: "Send invitation",
    sending: "Sending…",
    sent: "Invitation sent to {{email}}.",
    maxGroupsLabel: "Maximum groups",
    maxLicensesLabel: "Maximum licenses per group",
    groupNameLabel: "Initial group name (optional)",
    preGroupsLabel: "Groups to pre-create (optional)",
    preGroupsPlaceholder: "One name per line (or comma-separated)",
  },
  inviteLocalPartner: {
    heading: "Invite a local partner",
    emailLabel: "Email address",
    submit: "Send invitation",
    sending: "Sending…",
    sent: "Invitation sent to {{email}}.",
  },
  invitations: {
    heading: "Pending invitations",
    empty: "No pending invitations.",
    typeRegional: "Regional partner",
    typeLocal: "Local partner",
    resend: "Resend",
    cancel: "Cancel",
  },
} as const;

/** English strings for the consent screens (namespace: consents.*). */
export const consentsEn = {
  title: "Consent",
  loading: "Loading…",
  retry: "Retry",

  modeLabel: "Consent view",
  mode: {
    member: "My consent",
    partner: "Member consent",
  },

  member: {
    heading: "Consent to sharing my information in the Activateur",
    // Named group(s) interpolated as {{groups}}; singular/plural on {{count}} —
    // parity with Django's blocktrans in member-consent.html (K-4).
    intro_one:
      "You are part of the group {{groups}}, which gives you privileged access to Activateur. This partner has access to your profile information (excluding payment information) and to your progress in Activateur.",
    intro_other:
      "You are part of the groups {{groups}}, which give you privileged access to Activateur. These partners have access to your profile information (excluding payment information) and to your progress in Activateur.",
    introOptional:
      "Optional: you can grant additional access to {{groups}}. To do so, please check from the following list:",
    and: "and",
    noProfile:
      "You don't have an enterprise profile yet. Complete the identification step of your self-assessment first.",
    diagnostic:
      "Allow consultation of the information contained in the self-assessment, the action plan, and the achievement communication",
    edit:
      "Allow editing of the information contained in the self-assessment, the action plan, and the achievement communication",
    granted: "Granted",
    notGranted: "Not granted",
    continue: "Continue",
    error: "Something went wrong. Please try again.",
  },

  partner: {
    heading: "Member consent status",
    intro:
      "Look up what a member has agreed to share, by their enterprise profile.",
    profileIdLabel: "Enterprise profile ID",
    profileIdPlaceholder: "00000000-0000-0000-0000-000000000000",
    lookup: "Look up",
    looking: "Looking up…",
    notFound: "No profile found for that ID, or you don't have access.",
    enterprise: "Enterprise",
    viewConsent: "You may view this member's self-assessment",
    viewConsentYes: "Yes",
    viewConsentNo: "No",
    progress: "Progress shared",
    diagnostic: "Self-assessment shared",
    edit: "Editing allowed",
    yes: "Yes",
    no: "No",
  },
} as const;

/** Strings for the admin promo-code screen (namespace: promoCodes.*). */
export const promoCodesEn = {
  title: "Promotional codes",
  intro:
    "A promotional code linked to a partner group automatically attaches the business to that group and consumes a licence, just as a partner code would. An unlinked code grants a discount without being attached to anyone: the licences it consumes stay untraceable.",
  search: "Search",
  searchPlaceholder: "Code or group",
  state: "State",
  stateAll: "All states",
  stateBound: "Linked to a group",
  stateUnbound: "Not linked",
  group: "Group",
  groupAll: "All groups",
  groupSearch: "Search for a group…",
  noGroupMatch: "No group matches.",
  reset: "Reset",
  loading: "Loading…",
  loadError: "Could not load the codes.",
  empty: "No results",
  colCode: "Code",
  colDiscount: "Discount",
  colUses: "Uses",
  colState: "State",
  colGroup: "Partner group",
  colActions: "Actions",
  bound: "Linked",
  unbound: "Not linked",
  bind: "Link to a group",
  bindTitle: "Link a code to a group",
  bindHint:
    "Once linked, this code will attach the business to the chosen group and apply that group's discount.",
  bindSucceeded: "The code “{{code}}” is now linked.",
  bindFailed: "Could not link this code.",
  chooseGroup: "Choose a group",
  cancel: "Cancel",
  backfill: "Backfill",
  backfillTitle: "Member backfill",
  scanning: "Analysing Stripe invoices… ({{scanned}} analysed)",
  scanned: "{{count}} invoices analysed.",
  scanFailed: "The analysis failed.",
  confirmFailed: "The backfill failed.",
  noCandidates: "No members to backfill for this group.",
  colMember: "Member",
  colSubscription: "Subscription",
  colLicences: "Licences",
  overCap: "over cap",
  // The preview's subscription column — the member list's words, plus a
  // subscription still running. Keyed like licensing.preview_subscription_state.
  subscriptionState: {
    cancelled: "Cancelled",
    expired: "Expired",
    active: "Active",
  },
  overCapWarning:
    "The group is now over its licence cap. That is intended: those licences really were consumed.",
  addSelected: "Add {{count}} member(s)",
  joined: "{{count}} member(s) added.",
  bucketSkipped: "Already in another group — left alone",
  bucketUnresolved: "Linked codes Stripe does not know",
  alreadyIn: "already in “{{group}}”",
  neverSubscribed:
    "A business that never subscribed leaves no invoice and cannot be recovered here.",
} as const;
